import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, ImageOff, ShoppingCart } from "lucide-react";
import { useI18n } from "../../../i18n";
import { useCart } from "../../../context/CartContext";
import { fmtDate, fmtMoney, vatOf } from "../../../lib/format";
import { useCategories, useLastBought, useShop } from "../hooks";
import { PriceBreakdown, PriceLine } from "../ProductCard";
import { QtyStepper } from "../../../components/common/QtyStepper";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";
import { AddToList } from "../products/AddToList";
import { SafeImg } from "../../../components/common/SafeImg";
import { canPieces, unitName, unitPrice } from "../../../lib/uom";

const Gallery = ({ item, name }) => {
  const imgs = item.detail_url ? [item.detail_url] : item.photos || [];
  const [idx, setIdx] = useState(0);
  return (
    <div data-testid="product-gallery">
      <div className="aspect-square overflow-hidden rounded-2xl bg-white sm:aspect-[4/3]">
        {imgs.length ? <SafeImg key={idx} srcs={[imgs[idx], ...(item.photos || [])]} alt={name} className="h-full w-full object-cover animate-in fade-in duration-300" /> : <ImageOff className="m-auto mt-24 h-10 w-10 text-slate-300" />}
      </div>
      {imgs.length > 1 && (
        <div className="mt-2 flex gap-2">
          {imgs.map((src, i) => (
            <button key={src} type="button" onClick={() => setIdx(i)} data-testid={`gallery-thumb-${i}`} className={`h-16 w-16 overflow-hidden rounded-xl border-2 ${i === idx ? "border-brand" : "border-transparent"}`}>
              <img src={src} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PackTable = ({ item }) => {
  const { t } = useI18n();
  const rows = [
    [t("common.code"), item.code], [t("shop.unit"), unitName(t, item.unit)], item.units_per_box > 0 && [t("shop.unitsPerBox"), item.units_per_box],
    item.boxes_per_pallet > 0 && [t("shop.boxesPerPallet"), item.boxes_per_pallet], item.barcode && [t("shop.barcode"), item.barcode], [t("shop.vatRate"), `${item.vat_rate}%`],
  ].filter(Boolean);
  return (
    <table className="w-full text-sm" data-testid="packaging-table">
      <tbody className="divide-y divide-slate-100">
        {rows.map(([k, v]) => <tr key={k}><td className="py-2.5 text-slate-500">{k}</td><td className="py-2.5 text-right font-medium tabular-nums text-navy">{v}</td></tr>)}
      </tbody>
    </table>
  );
};

export default function ProductDetail() {
  const { code } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const shop = useShop();
  const cats = useCategories();
  const last = useLastBought();
  const { qtyOf, setQty, count, modeOf, setMode } = useCart();
  const item = shop.byCode[code];
  const price = shop.priceMap[code];

  if (shop.isLoading) return <div className="grid gap-6 md:grid-cols-2"><SkeletonBlock className="aspect-square" /><SkeletonBlock className="h-80" /></div>;
  if (!item || !price) return <EmptyState icon={ImageOff} title={t("shop.notFound")} testId="product-not-found" action={<Link to="/app/products" className="text-sm font-semibold text-brand">{t("shop.browse")}</Link>} />;

  const name = item[`name_${lang}`];
  const catName = (c) => (cats.data || []).find((x) => x.code === c)?.[`name_${lang}`];
  const lb = last.data?.[code];
  const qty = qtyOf(code);
  const mode = modeOf(code, item);
  const unit = unitPrice(item, price, mode);
  return (
    <div className="space-y-5" data-testid="product-detail">
      <button type="button" onClick={() => nav(-1)} data-testid="product-back" className="flex h-11 items-center gap-1 text-sm font-medium text-slate-600 hover:text-navy"><ArrowLeft className="h-4 w-4" /> {t("shop.back")}</button>
      <div className="grid gap-6 md:grid-cols-2">
        <Gallery item={item} name={name} />
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand">{[item.brand, catName(item.family), catName(item.subcategory)].filter(Boolean).join(" · ")}</p>
            <h1 className="mt-1 text-2xl font-bold leading-tight text-navy sm:text-3xl" data-testid="product-name">{name}</h1>
            {lb && <p className="mt-2 text-sm text-slate-600" data-testid="product-last-bought">{t("shop.lastBought", { date: fmtDate(lb.date), qty: lb.qty_boxes, unit: t(lb.uom === "piece" ? "x.uom.piece" : "x.uom.box") })}</p>}
          </div>
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-500">{t("shop.priceNet")}</p>
            <PriceLine item={item} price={price} mode={mode} />
            <PriceBreakdown item={item} price={price} mode={mode} />
            <p className="mt-1 text-xs text-slate-500">{t("common.inclVat")}: {fmtMoney(unit + vatOf(unit, item.vat_rate))}</p>
            <div className="mt-4 flex items-end gap-2">
              <div className="flex-1"><QtyStepper size="lg" value={qty} onChange={(q) => setQty(code, q, item)} testId="detail-qty" mode={mode} onMode={canPieces(item) ? (m) => setMode(code, m) : undefined} /></div>
              <AddToList itemCode={code} qty={qty || 1} />
            </div>
            {count > 0 && <Link to="/app/cart" data-testid="detail-go-cart" className="mt-3 flex h-11 items-center justify-center gap-2 rounded-full border border-navy/15 text-sm font-semibold text-navy hover:bg-slate-50"><ShoppingCart className="h-4 w-4" /> {t("shop.cart")} ({count})</Link>}
          </div>
          <section><h2 className="text-base font-semibold text-navy md:text-lg">{t("shop.description")}</h2><p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="product-description">{item[`description_${lang}`]}</p></section>
          <section><h2 className="text-base font-semibold text-navy md:text-lg">{t("shop.packaging")}</h2><PackTable item={item} /></section>
          {item.safety_sheet_url && (
            <a href={item.safety_sheet_url} target="_blank" rel="noreferrer" data-testid="product-sds-link" className="flex h-11 items-center gap-2 text-sm font-semibold text-brand hover:underline"><FileText className="h-4 w-4" /> {t("common.safetySheet")}</a>
          )}
        </div>
      </div>
    </div>
  );
}
